// VoucherBackupBanner — Jun 2026 (Randy).
// When a creator has money sitting in a voucher AND an active BYOK key for that
// SAME provider, the voucher is "hidden" (the green BYOK tile shows instead of
// the red voucher tile), so they may not notice the funds. This calm banner
// surfaces it: it's wise to keep them as a backup (auto-used if your key fails),
// but you can transfer to earnings if you'd rather. Detected per provider; if
// more than one applies they're combined into a single banner (up to three).
import { useEffect, useState } from "react";
import { Wallet, ArrowsLeftRight, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PROVIDER_LABELS = {
  gemini: "Google Gemini",
  anthropic: "Anthropic (Claude)",
  openai: "OpenAI",
};

const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function VoucherBackupBanner() {
  const [flagged, setFlagged] = useState([]); // [{provider, cents}]
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("voucher_backup_banner_dismissed") === "1"
  );

  const load = async () => {
    try {
      const r = await api.get("/me/voucher-summary");
      const byok = r.data?.byok_status || {};
      const totals = r.data?.provider_totals_cents || {};
      const hits = ["gemini", "anthropic", "openai"]
        .filter((p) => byok[p] && (totals[p] || 0) > 0)
        .map((p) => ({ provider: p, cents: totals[p] || 0 }));
      setFlagged(hits);
    } catch {
      /* opportunistic banner — silent fail */
    }
  };

  useEffect(() => { load(); }, []);

  if (dismissed || flagged.length === 0) return null;

  const totalCents = flagged.reduce((a, f) => a + f.cents, 0);
  const phrase = flagged
    .map((f) => `${fmt$(f.cents)} in your ${PROVIDER_LABELS[f.provider]} voucher`)
    .reduce((acc, cur, i, arr) => {
      if (i === 0) return cur;
      if (i === arr.length - 1) return `${acc}${arr.length > 2 ? "," : ""} and ${cur}`;
      return `${acc}, ${cur}`;
    }, "");

  const handleDismiss = () => {
    sessionStorage.setItem("voucher_backup_banner_dismissed", "1");
    setDismissed(true);
  };

  // Oct 2026 (Randy) — two destinations. An immediate cash-out fires its own
  // Stripe transfer, so it carries a $10 minimum; "next payout" has none because
  // it rides the commission batch that was going out anyway. Gifted (trial or
  // comp) credit is never withdrawable by either route — the server excludes it.
  const handleTransfer = async (destination = "now") => {
    const immediate = destination === "now";
    const ok = window.confirm(
      immediate
        ? `Transfer ${fmt$(totalCents)} from your backup voucher${flagged.length > 1 ? "s" : ""} to your earnings now? ` +
          `Your own key${flagged.length > 1 ? "s" : ""} stays connected — you just won't have this reserve to fall back on if it fails.`
        : `Add ${fmt$(totalCents)} to your next commission payout? It goes out with the next batch instead of as its own transfer.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post("/me/voucher/flush-byok-backups", { destination });
      const swept = r.data?.swept_cents || 0;
      const dest = r.data?.destination;
      const gifted = r.data?.gifted_left_cents || 0;
      if (swept > 0) {
        toast.success(
          dest === "next_payout"
            ? `${fmt$(swept)} added to your next commission payout.`
            : dest === "pending_connect" || dest === "pending_voucher_sweep"
              ? `${fmt$(swept)} queued — it lands in your earnings the moment your bank payout is connected.`
              : `${fmt$(swept)} moved to your earnings.`
        );
      } else if (gifted > 0) {
        toast.info("That credit was a gift — it can be spent, but not withdrawn.");
      } else {
        toast.info("Nothing to transfer.");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't transfer right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-4 rounded-xl border border-[#A89360]/50 bg-[#FAF6EA] px-4 py-3.5 flex items-start gap-3"
      data-testid="voucher-backup-banner"
    >
      <ShieldCheck size={20} weight="duotone" className="text-[#7A5C20] shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#5C4612]" data-testid="voucher-backup-title">
          You have backup credit in your voucher{flagged.length > 1 ? "s" : ""}.
        </div>
        <div className="text-xs text-[#6B5A2A] mt-1 leading-relaxed">
          You've got {phrase}. Because your own key{flagged.length > 1 ? "s are" : " is"} connected for{" "}
          {flagged.length > 1 ? "those providers" : "that provider"}, this credit is tucked away — easy to miss.
          It's wise to <strong>keep it here as a backup</strong>: if your key ever fails or runs dry, your silos
          automatically fall back to this reserve and keep answering. But it's your call — you can move it to your
          earnings instead.
        </div>
        <div className="mt-2.5 flex gap-2 flex-wrap items-center">
          <button
            onClick={() => handleTransfer("now")}
            disabled={busy}
            data-testid="voucher-backup-transfer"
            className="text-xs px-3 py-1.5 bg-white border border-[#A89360] text-[#7A5C20] rounded font-semibold hover:bg-[#FAF2DA] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <ArrowsLeftRight size={12} weight="bold" />
            {busy ? "Transferring…" : `Transfer ${fmt$(totalCents)} to earnings`}
          </button>
          <button
            onClick={() => handleTransfer("next_payout")}
            disabled={busy}
            data-testid="voucher-backup-next-payout"
            title="Rides the next commission batch — no minimum, no separate transfer fee"
            className="text-xs px-3 py-1.5 bg-white border border-[#A89360] text-[#7A5C20] rounded hover:bg-[#FAF2DA] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <ArrowsLeftRight size={12} weight="duotone" />
            Add to next payout
          </button>
          <button
            onClick={handleDismiss}
            data-testid="voucher-backup-dismiss"
            className="text-xs px-3 py-1.5 text-[#7A5C20] rounded hover:bg-[#FAF2DA] inline-flex items-center gap-1.5"
          >
            <Wallet size={12} weight="duotone" /> Keep as backup
          </button>
        </div>
      </div>
    </div>
  );
}
